<template>
  <div>
    <ModalView :isVisible="isModalVisible" :title="'Payment'" @close="closeCardModal">
      <div class="payment-container-row">
        <div class="header-payment-row">
          <div>
            <img v-if="product?.images?.length" :src="product.images[0].urlDetailed" alt="" />
            <img v-else-if="product?.thumbnail_image?.length" :src="product.thumbnail_image[0].url" alt="" />
          </div>
          <div>
            <p v-if="contactDetails.mode == 'subscription'">
              Subscribe to {{ product.product_name ? product.product_name : product.title }} and 1 more
            </p>
            <div v-else>
              <p class="product-text">{{ product.product_name ? product.product_name : product.title }}</p>
              <div v-if="contactDetails?.shipping">
                <p class="text-5">{{ contactDetails?.shipping }}</p>
              </div>
              <div v-if="contactDetails?.shipping">
                <p class="mb-1 text-5">Total</p>
              </div>

              <p class="product-title mt-2">
                You are purchasing a {{ product.product_name ? product.product_name : product.title }} for
                {{ profile.name }} – {{ profile.barcode }}
              </p>
            </div>
            <div class="payment-flex-row" v-if="contactDetails.mode == 'subscription'">
              <div>
                <p>{{ product.product_name ? product.product_name : product.title }}</p>
                <p>Every {{ contactDetails.interval }} Month</p>
              </div>
              <div class="d-flex">
                <p>{{ productPrice }}</p>
              </div>
            </div>
            <div class="payment-flex-row" v-if="contactDetails.mode == 'subscription'">
              <div>
                <p>Do it Yourself (Fingerprick)</p>
                <p>Every {{ contactDetails.interval }} Month</p>
              </div>
              <div>
                <p>£0.00</p>
              </div>
            </div>
          </div>
          <div>
            <p class="text-4">{{ productPrice }}</p>
            <p v-if="contactDetails.mode == 'subscription'">Every {{ contactDetails.interval }} Month</p>
            <p v-if="contactDetails?.shipping" class="text-5">
              {{ shippingPriceText }}
            </p>
            <p class="mb-0 text-5"
              v-if="product.variants?.[0] && productPrice !== product.variants[0].currencySymbol.raw + finalPrice">
              {{ product.variants[0].currencySymbol.raw + finalPrice }}
            </p>
          </div>
        </div>

        <div class="card-details-container mt-3">
          <h4>Card Details:</h4>

          <!-- Cardholder Name and Expiry -->
          <div class="card-row">
            <div id="card-number-element" class="card-input"></div>
          </div>

          <!-- Card Number and CVV -->
          <div class="card-row">
            <div id="card-expiry-element" class="card-input-date"></div>
            <div id="card-cvc-element" class="card-input-date"></div>
            <input v-model="customZip" class="card-input-date" placeholder="Zip Code" />
          </div>
        </div>

        <!-- Stripe Card Element -->
        <p v-if="errorMessage" class="error">{{ errorMessage }}</p>

        <div class="action-cta">
          <!-- Wrapped: BloodTestDetails.scss colours .action-cta's direct buttons by position -->
          <div class="payment-actions">
            <button class="ldna-btn" @click="subscribe" :disabled="isProcessing">
              {{ isProcessing ? 'Processing...' : 'Pay by card' }}
            </button>
            <button type="button" class="ldna-link payment-method" @click="backModal">Change payment method</button>
          </div>
        </div>
      </div>
    </ModalView>
  </div>
</template>

<script>
import "@/styles/pages/BloodTestDetails.scss";
import Icon from "@/assets/images/Health/BloodDetails/imagex90 (2).png";
import { getPrice } from "@/services/price.js";
import { loadStripe } from "@stripe/stripe-js";
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import ModalView from "@/components/Modal/ModalView.vue";
import store from '@/store';
// import { prop } from "lodash/fp";

import ProductService from "@/services/product";
import { CreateStripeOrder } from "@/services/product";

export default {
  name: 'PaymentModal',
  components: {
    ModalView,
  },
  props: {
    isModalVisible: Boolean,
    connectionIcon1: String,
    connectionIcon2: String,
    contactDetails: Object,
    product: Object,
    delivered: Object,
    isForKit: Boolean,
    barcode: { type: String, default: null },
  },
  setup(props, { emit }) {
    const icon = Icon;
    const stripe = ref(null);
    const errorMessage = ref('');
    const isProcessing = ref(false);
    const profile = computed(() => store.getters.profile.profile);
    const effectiveBarcode = computed(() => props.barcode || profile.value.barcode);
    const cardNumberElement = ref(null);
    const cardExpiryElement = ref(null);
    const cardCvcElement = ref(null);
    const customZip = ref('');

    const unmountStripeElements = () => {
      if (cardNumberElement.value) {
        cardNumberElement.value.unmount();
        cardNumberElement.value = null;
      }
      if (cardExpiryElement.value) {
        cardExpiryElement.value.unmount();
        cardExpiryElement.value = null;
      }
      if (cardCvcElement.value) {
        cardCvcElement.value.unmount();
        cardCvcElement.value = null;
      }
    };

    const mountStripeElements = async () => {
      await nextTick();
      if (!props.isModalVisible) return;

      if (!document.getElementById('card-number-element')) {
        return;
      }

      if (!stripe.value) {
        stripe.value = await loadStripe(process.env.VUE_APP_STRIPE_ID);
      }

      unmountStripeElements();

      if (!stripe.value) return;
      const elements = stripe.value.elements();
      cardNumberElement.value = elements.create('cardNumber', {
        style: { base: { fontSize: '16px', color: '#32325d' } },
        placeholder: 'Card Number',
      });
      cardNumberElement.value.mount('#card-number-element');

      cardExpiryElement.value = elements.create('cardExpiry', {
        style: { base: { fontSize: '16px', color: '#32325d' } }
      });
      cardExpiryElement.value.mount('#card-expiry-element');
      cardCvcElement.value = elements.create('cardCvc', {
        style: { base: { fontSize: '16px', color: '#32325d' } }
      });
      cardCvcElement.value.mount('#card-cvc-element');
    };

    watch(() => props.isModalVisible, async (isVisible) => {
      if (isVisible) {
        await mountStripeElements();
      } else {
        unmountStripeElements();
      }
    });

    onBeforeUnmount(() => {
      unmountStripeElements();
    });

    /**
     * What the collection method costs on the payment summary.
     *
     * A digital collection is free and carries no shipping price, so contactDetails.price
     * is null - shippingPrices is only ever populated for a shippable product. This row
     * used to concatenate it onto the currency symbol, so every genetic report showed its
     * collection as "£null" on the confirmation before paying.
     */
    const shippingPriceText = computed(() => {
      const price = props.contactDetails?.price;
      if (price === null || price === undefined || Number(price) === 0) {
        return "FREE";
      }
      const symbol = props.product?.variants?.[0]?.currencySymbol?.raw || "";
      return symbol + price;
    });

    const finalPrice = computed(() => {
      let product_price = 0;
      let totalPrice = 0;
      if (props.product?.variants?.[0]) {
        product_price = props.product.variants[0].price?.base ? (props.product.variants[0].price.base / 100) : 0;
        const contact_price = props.contactDetails.price ? parseFloat(props.contactDetails.price) : 0;
        totalPrice = (product_price + contact_price).toFixed(2) * 100;
        return totalPrice / 100
      }
      else {
        product_price = props.product.price ? props.product.price / 100 : 0;
        const contact_price = props.contactDetails.price ? parseFloat(props.contactDetails.price) : 0;
        totalPrice = (product_price + contact_price).toFixed(2) * 100;
        return totalPrice / 100
      }
    })

    const productPrice = computed(() => {
      if (getPrice(props.product) === undefined) {
        if (props.product?.variants?.[0]?.price?.isOnSale) {
          return props.product.variants[0].currencySymbol.raw + props.product.variants[0].price.sale / 100;
        }
        if (props.product?.variants?.[0]?.price?.base) {
          return props.product.variants[0].currencySymbol.raw + props.product.variants[0].price.base / 100;
        }
        return 0;
      }
      return getPrice(props.product);
    });

    const closeCardModal = (value = null) => {
      emit('closeCardModal', value);
      emit('close-card-modal', value);
      emit('closePaymentCardModal', value);
      emit('close-payment-card-modal', value);
    };
    const backModal = () => {
      emit('back');
    }

    // The logged-in account's name and email and the ZIP typed above, for Stripe Radar. Empty
    // values are left out: Stripe rejects an empty email.
    const billingDetails = () => {
      const account = store.getters.account || {};
      const name = [account.firstName, account.lastName].filter(Boolean).join(' ');
      return {
        name: name || undefined,
        email: account.email || undefined,
        address: { postal_code: customZip.value.trim() },
      };
    };

    const subscribe = async () => {
      isProcessing.value = true;
      errorMessage.value = '';
      // let data = null;
      if (!customZip.value || customZip.value.trim() === '') {
        errorMessage.value = 'Please enter a ZIP code.';
        isProcessing.value = false;
        return;
      }
      try {
        if (!stripe.value || !cardNumberElement.value) {
          errorMessage.value = 'Payment form is not ready. Please try again.';
          isProcessing.value = false;
          return;
        }
        let product_price = 0;
        let totalPrice = 0;
        if (props.product.variants) {
          product_price = props.product.variants[0].price.base ? (props.product.variants[0].price.base / 100) : 0;
          const contact_price = props.contactDetails.price ? parseFloat(props.contactDetails.price) : 0;
          totalPrice = (product_price + contact_price).toFixed(2) * 100;
          finalPrice.value = totalPrice
        }
        else {
          product_price = props.product.price ? props.product.price / 100 : 0;
          const contact_price = props.contactDetails.price ? parseFloat(props.contactDetails.price) : 0;
          totalPrice = (product_price + contact_price).toFixed(2) * 100;
          finalPrice.value = totalPrice
        }
        const code = 'AX';
        const order = new CreateStripeOrder(
          effectiveBarcode.value,
          code,
          props.delivered,
          props.contactDetails.shipping,
          props.product,
          Math.round(totalPrice)
        );

        const data = await ProductService.createPaymentIntentForOrder(order);
        if (data) {
          const response = await ProductService.chargeStripeOrder(data.paymentIntentclientSecret, stripe.value.confirmCardPayment, cardNumberElement.value, billingDetails())
          emit('order', data);
          emit('closePaymentCardModal', response);

        }
      } catch (err) {
        errorMessage.value = err.message;
      } finally {
        isProcessing.value = false;
      }
    };

    return {
      icon,
      errorMessage,
      isProcessing,
      subscribe,
      productPrice,
      finalPrice,
      shippingPriceText,
      backModal,
      profile,
      expiryDate: ref(''),
      cvv: ref(''),
      cardNumberElement,
      cardExpiryElement,
      customZip,
      closeCardModal
    };
  }
};
</script>
<style scoped>
h4 {
  font-size: 1.2em;
  margin-bottom: 20px;
}

.card-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
}

.card-input {
  width: 67%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 7px;
  font-size: 14px;
}

.card-input-date {
  width: 30%;
  /* Adjust the width to leave space between two inputs */
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 7px;
  /* 16px, as the Stripe fields beside it: iPhones zoom the page into smaller fields */
  font-size: 16px;
}

.payment-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
}

/* As wide as the card fields above it, not the usual 452px */
.payment-actions .ldna-btn.ldna-btn {
  max-width: none;
}

/* Phones: the sheet has its own padding, the summary is a compact row, and the card fields
   share the width evenly */
@media (max-width: 598px) {
  .payment-container-row {
    padding: 0;
  }

  .payment-container-row .header-payment-row {
    display: grid;
    align-items: center;
    grid-template-columns: 56px minmax(0, 1fr) auto;
    gap: 12px;
    padding: 12px;
    border-radius: 12px;
  }

  .card-row {
    gap: 8px;
  }

  .card-input-date {
    width: auto;
    flex: 1 1 0;
    min-width: 0;
  }
}

#card-number-element {
  width: 100%;
  /* Same styling as the other inputs */
}

select.card-input {
  width: 100%;
}

.error {
  color: #fa755a;
  font-size: 12px;
  margin-top: 10px;
}
</style>
